/**
 * useFavicon — swap the document's favicon based on which surface
 * the React app is currently rendering.
 *
 * Bas: "I would like to have another color of the icon for each type of
 * website. For the public website, Admin site, Portal site and the App
 * site each another color. Then I can see easily which is which."
 *
 * Why a JS hook rather than per-surface static .ico files:
 *   - The whole app is a single SPA off /index.html — we can't ship
 *     different <link rel="icon"> in HTML per route.
 *   - Generating one SVG template + tinting at runtime means there's
 *     one source of truth for the icon shape; adding a 5th surface
 *     later is a one-line palette addition.
 *
 * Design choice for visibility at 16-32 px:
 *   - SOLID COLORED ROUNDED SQUARE background → the dominant pixel
 *     mass at favicon size. Browser tab-strip glance: instant
 *     "which surface is this".
 *   - Simplified phone icon in white on top — keeps brand recognition
 *     at apple-touch-icon size (180 px) and PWA install icon (192/512).
 *   - Heart stays coral on EVERY surface — same warmth cue as the full
 *     logo. Cheap brand consistency without sacrificing the colour-code.
 *   - Soundwaves OMITTED — too thin to survive 16 px resampling and
 *     would muddy the silhouette.
 *
 * The static <link rel="icon"> in web/public/index.html stays as the
 * pre-mount default (matches the public-surface palette). The hook
 * overrides on first render of any surface; there's a momentary flash
 * on Admin/Portal/App as React mounts, which is acceptable.
 */
import { useEffect } from 'react';

// Brand palette per surface. Pull from the design brief in the GIMP
// package (#557 voice admin work surfaced these as the canonical
// brand colours):
//   teal:    #2A9D9B   — primary brand, used for Public surfaces
//   navy:    #153E7A   — Admin (matches the navy "AI Voice Companion"
//                        subtitle treatment)
//   coral:   #F27F6D   — Portal (matches the heart accent)
//   seafoam: #5FB7AF   — App (lighter teal, distinct from Public's deeper teal)
//
// The phone outline + face are rendered in #FFF on top so they read
// against any of these backgrounds.
const SURFACE_PALETTE = {
  public:  '#2A9D9B',
  admin:   '#153E7A',
  portal:  '#F27F6D',
  app:     '#5FB7AF',
};

const HEART_COLOR  = '#F27F6D';   // stays coral on every surface — brand warmth
const ICON_COLOR   = '#FFFFFF';   // white phone outline + face on the colored bg

/**
 * Build an SVG string for the favicon. Returns a data: URL that can be
 * dropped straight into a `<link rel="icon" href=...>`. The viewBox is
 * 500×500 (square — mandatory for a sane favicon; browsers will pad a
 * non-square favicon with surprising letterboxing).
 */
function buildFaviconSvg(bgColor) {
  // Colour-string injected into the SVG body. Keep this function pure
  // so the cache key (memoised below) is just the bgColor.
  return (
    '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 500 500">' +
      // Rounded-square background — radius is generous (~17% of side)
      // so the shape reads as "app icon" not "card".
      `<rect width="500" height="500" rx="85" ry="85" fill="${bgColor}"/>` +
      // Phone icon — translated to centre vertically inside the 500-tall
      // viewBox (icon native height ~350, so 75 padding top/bottom).
      '<g transform="translate(125,75)">' +
        // Phone outline.
        `<rect x="0" y="0" rx="42" ry="42" width="250" height="350" fill="none" stroke="${ICON_COLOR}" stroke-width="18"/>` +
        // Camera notch dot at the top (white over the colored bg means
        // it shows as a tiny white dash — more visible than the
        // original gradient version).
        `<rect x="98" y="315" rx="7" ry="7" width="55" height="14" fill="${ICON_COLOR}"/>` +
        // Speech-bubble face outline.
        `<path d="M58,112 C95,58 190,76 205,150 C219,218 163,255 104,236 L61,260 L68,216 C35,190 31,151 58,112Z" fill="none" stroke="${ICON_COLOR}" stroke-width="14" stroke-linejoin="round"/>` +
        // Eyes + smile.
        `<path d="M88,154 C104,130 128,130 142,154" fill="none" stroke="${ICON_COLOR}" stroke-width="10" stroke-linecap="round"/>` +
        `<path d="M104,192 C124,214 158,214 178,192" fill="none" stroke="${ICON_COLOR}" stroke-width="10" stroke-linecap="round"/>` +
        // Heart — stays coral on every surface for warmth + brand
        // continuity with the full wordmark.
        `<path d="M225,75 C247,42 290,52 289,91 C289,120 245,143 225,158 C204,142 160,121 160,91 C160,52 203,43 225,75Z" fill="${HEART_COLOR}" opacity="0.95"/>` +
      '</g>' +
    '</svg>'
  );
}

// Tiny memoisation so we don't re-encode + re-attach the same SVG on
// every re-render of a surface (which would happen on every state
// change in TabletApp / Portal). Keyed by surface name; the SVG
// content is deterministic per surface.
const _cache = {};

function getFaviconHref(surface) {
  if (_cache[surface]) return _cache[surface];
  const bg = SURFACE_PALETTE[surface] || SURFACE_PALETTE.public;
  const svg = buildFaviconSvg(bg);
  // encodeURIComponent keeps the URL safe; data: URLs accept un-base64'd
  // SVG when properly encoded, and skipping base64 saves a few bytes.
  const href = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg);
  _cache[surface] = href;
  return href;
}

/**
 * React hook — call once near the top of each surface's root component:
 *   useFavicon('public' | 'admin' | 'portal' | 'app')
 *
 * Falls back to the public palette if the surface name is unknown,
 * so a typo doesn't blank the favicon.
 */
export function useFavicon(surface) {
  useEffect(() => {
    const href = getFaviconHref(surface);
    // Reuse the existing <link rel="icon"> from index.html if present
    // (it's the public-surface default); otherwise create one. Only
    // touch the first match — browsers honour the first link[rel=icon].
    let link = document.querySelector('link[rel="icon"]');
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }
    link.type = 'image/svg+xml';
    link.href = href;
  }, [surface]);
}

// Exported for tests + future callers that want the raw URL (e.g. the
// admin panel could surface "current favicon palette" for debugging).
export { SURFACE_PALETTE, getFaviconHref };
